import React from "react";
import { Formik } from "formik";
import {
  Button,
  TextField,
  Grid,
  Container,
  Typography,
  MenuItem
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { submissionValidationSchema } from './Validation';
import { stateNameList } from '../../assets/stateNameList';
import ReactTooltip from 'react-tooltip';
import Modal from './Modal';

const stylesFunc = makeStyles(() => ({
  root: {
    '& input:valid + fieldset': { borderColor: 'green', borderWidth: 1 },
    '& input:invalid:focus + fieldset': { borderColor: 'red', borderWidth: 1 },
    '& input:valid:focus + fieldset': {
      borderLeftWidth: 6,
      borderColor: 'green',
      padding: '4px !important',
    },
  },
  wrapper: {
    marginTop: "3rem",
    height: "calc(200vh - 19.0625rem)",
    textAlign: 'start',
    marginBottom: "9rem",
  },
  formHeader: { textAlign: 'center', marginBottom: '1rem', marginTop: '1rem' },
  header: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  scholarshipAlert: {
    position: 'relative',
    marginTop:'0.15rem',
    height:'4.5em',
    color: '#FF9800',
    textAlign:'center',
    border: '1px solid #FF9800',
    borderRadius:'5px'
  },
  warning: { padding: '4px !important', position: 'relative' },
  reviewButton: {
    marginTop: '2rem',
    marginBottom: '1em',
    background: '#14688D',
    color: '#fff',
    '&:hover': { backgroundColor: '#fff', color: '#14688D' }
  },
}));

const isMilitary = [ { value: 1, opt: 'Yes' }, { value: 2, opt: 'No' } ];

const FormBasics = ({ navigation, inputData, setForm }) => {
  const formStyles = stylesFunc();
  const { next } = navigation;

  // ✅ Validate ONLY the fields on this page
  const basicSchema = submissionValidationSchema.pick([
    'firstName',
    'lastName',
    'legalStatus',
    'dob',
    'schoolEmail',
    'personalEmail',
    'phone',
    'address',
    'city',
    'zipcode',
    'state',
    'school',
    'progress',
    'program',
    'studentId',
    'startYear',
    'grade',
    'militaryAffiliation',
  ]);

  return (
    <Container className={ formStyles.wrapper } maxWidth="sm">
      <div className={ formStyles.header }>
        <i className="fas fa-user-graduate fa-5x" style={{ color: "#14688D" }}></i>
        <Typography className={ formStyles.formHeader } style={{ color: "#34455E" }} variant="h6">
          Please reach out in case of any questions <Typography className={ formStyles.formHeader } style={{ color: "#34455E" }} variant="h5">scholarship@blue-tulip.org</Typography>
        </Typography>
        <Typography>
          <Grid container spacing={1}>
            <Grid item xs={10} className={ formStyles.scholarshipAlert }>
              <Grid className={ formStyles.warning }>
                Please be prepared to upload 2 documents (essay and tuition documents) to complete the application.
              </Grid>
            </Grid>
            <Grid item xs={2}><Modal /></Grid>
          </Grid>
        </Typography><br />
      </div>

      <Formik
        initialValues={inputData}          // keep wizard values if user goes back
        enableReinitialize
        validationSchema={basicSchema}     // 🔑 only page-1 fields
        validateOnMount
        onSubmit={(values) => {
          // merge all current values into wizard state (react-hooks-helper expects event-like)
          Object.entries(values).forEach(([name, value]) =>
            setForm({ target: { name, value } })
          );
          next();
        }}
      >
        {(formik) => {
          // bind helper: keep your UX, update BOTH Formik and wizard state
          const bind = (name, ui = {}) => ({
            name,
            value: formik.values[name] ?? "",
            onChange: (e) => { formik.handleChange(e); setForm(e); },
            onBlur: formik.handleBlur,
            error: Boolean(formik.touched[name] && formik.errors[name]),
            helperText:
              (formik.touched[name] && formik.errors[name]) ||
              (formik.values[name] === "" ? ui.hint || null : null),
            variant: "outlined",
            fullWidth: true,
            ...ui.extra,
          });

          return (
            <form onSubmit={formik.handleSubmit} className={ formStyles.root }>
              <Grid container spacing={3} justify="center">
                <Grid item xs={6}>
                  <TextField required label="First Name" type="text"
                    {...bind("firstName", { hint: "Your first name" })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField required label="Last Name" type="text"
                    {...bind("lastName", { hint: "Your last name" })} />
                </Grid>

                <Grid item xs={6}>
                  <TextField required label="Legal Status" type="text"
                    {...bind("legalStatus", { hint: "Your legal status in the US" })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField required label="Date of Birth" type="date"
                    {...bind("dob", { hint: "Your date of birth", extra: { InputLabelProps: { shrink: true } } })} />
                </Grid>

                <Grid item xs={6}>
                  <TextField required label="School Email Address" type="email"
                    {...bind("schoolEmail", { hint: "Your school email address" })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField required label="Personal Email Address" type="email"
                    {...bind("personalEmail", { hint: "Your personal email address" })} />
                </Grid>

                <Grid item xs={6}>
                  <TextField required label="Phone Number"
                    {...bind("phone", { hint: "Should be like +17034567890" })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField required label="Street Address" type="text"
                    {...bind("address", { hint: "Your street address here in the US" })} />
                </Grid>

                <Grid item xs={3}>
                  <TextField required label="City" type="text"
                    {...bind("city")} />
                </Grid>
                <Grid item xs={3}>
                  <TextField required label="Zip Code" type="number"
                    {...bind("zipcode")} />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    className={ formStyles.root }
                    select
                    required
                    label="State"
                    {...bind("state", { hint: "The state you are living in the US" })}
                  >
                    {stateNameList.map((option) => (
                      <MenuItem key={option.value} value={option.stateName}>
                        {option.stateName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={6}>
                  <TextField required label="Current University / College" type="text"
                    {...bind("school", { hint: "Your current university or college name" })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField required label="Current Program" type="text"
                    {...bind("program", { hint: "Your current program name" })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField required label="So far progress in the program" type="text"
                    {...bind("progress", { hint: "Your progress so far" })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField required label="University ID Number" type="text"
                    {...bind("studentId", { hint: "Your university ID" })} />
                </Grid>

                <Grid item xs={4}>
                  <TextField required label="Year Started" type="number"
                    {...bind("startYear", { hint: "Your program start date" })} />
                </Grid>
                <Grid item xs={4}>
                  <TextField required label="Average Grade" type="text"
                    {...bind("grade", { hint: "Average Grade of Previous Semester" })} />
                </Grid>

                <Grid item xs={4} data-tip data-for='global'>
                  <TextField
                    className={ formStyles.root }
                    select
                    required
                    label="Military Affiliation"
                    {...bind("militaryAffiliation", { hint: "Former military or immediate relative ?" })}
                  >
                    {isMilitary.map((option) => (
                      <MenuItem key={option.value} value={option.opt}>
                        {option.opt}
                      </MenuItem>
                    ))}
                  </TextField>
                  <ReactTooltip id="global" type='info' place='top' effect='solid'>
                    <p>Refers to individuals that have some type of connection to military service.</p>
                    <p>Personal service (prior service in Turkish Armed Forces (TAF)),
                      and family member service (a spouse or parent who has personally served) are included regardless of 
                      length of time, branch, or type. </p>
                    <p>Also all ex-cadets in TAF are considered Military-Affiliated due 
                      to their past service as a student.</p>
                  </ReactTooltip>
                </Grid>

                <Grid item xs={4}>
                  <Button
                    className={ formStyles.reviewButton }
                    type="submit"            
                    variant="contained"
                    fullWidth
                    disabled={formik.values.militaryAffiliation === "No"}
                  >
                    Next
                  </Button>
                </Grid>
              </Grid>
            </form>
          );
        }}
      </Formik>
    </Container>
  );
};

export default FormBasics;
